import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Badge, Button, Group, Paper, PasswordInput, Stack, Text, TextInput } from "@mantine/core"
import { useCallback, useState } from "react"
import type { useCompanyApi } from "../../../hooks/useCompanyApi"
import { testAdminTeamsIntegration, testTeamsIntegration } from "../../../lib/api"
import { extractErrorMessage } from "../../../lib/api/errors"
import { SettingsReadOnlyNotice } from "./SettingsReadOnlyNotice"

interface IProps {
  settings: Record<string, string>
  /** Secret keys reported as already-configured (masked) by the backend. */
  configuredSecrets: Set<string>
  saving: boolean
  company: ReturnType<typeof useCompanyApi>
  isAdminOnly?: boolean
  /** Owner/admin (or super-admin) may configure Teams; a plain member is read-only. Default true. */
  canManage?: boolean
  onUpdateSetting: (key: string, value: string) => void
  onSave: (keys?: string[]) => void
}

export function TeamsIntegrationSection({
  settings,
  configuredSecrets,
  saving,
  company,
  isAdminOnly,
  canManage = true,
  onUpdateSetting,
  onSave,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ valid: boolean; error?: string } | null>(null)

  // The client secret is masked by the backend GET. When it's configured but
  // the user hasn't retyped it, the field is blank and Test stays disabled
  // (canTest already requires a non-empty secret) — surface that state.
  const secretConfiguredUnchanged = configuredSecrets.has("teams_secret") && (settings.teams_secret ?? "").trim() === ""

  const handleTest = useCallback(async (): Promise<void> => {
    const tenantId = settings.teams_tenant_id ?? ""
    const appId = settings.teams_app_id ?? ""
    const secret = settings.teams_secret ?? ""
    if (!tenantId || !appId || !secret) return
    setTesting(true)
    setTestResult(null)
    try {
      const result =
        isAdminOnly || !company
          ? await testAdminTeamsIntegration(tenantId, appId, secret)
          : await testTeamsIntegration(company, tenantId, appId, secret)
      setTestResult(result)
    } catch (err) {
      setTestResult({ valid: false, error: extractErrorMessage(err) })
    } finally {
      setTesting(false)
    }
  }, [company, isAdminOnly, settings.teams_tenant_id, settings.teams_app_id, settings.teams_secret])

  const teamsKeys = ["teams_tenant_id", "teams_app_id", "teams_secret", "teams_watched_user_ids", "teams_webhook_url"]
  const canTest =
    !!(settings.teams_tenant_id ?? "").trim() &&
    !!(settings.teams_app_id ?? "").trim() &&
    !!(settings.teams_secret ?? "").trim()

  return (
    <Stack gap="md">
      <Alert variant="light" color="info">
        <Text size="sm">
          <Trans>
            Configure Microsoft Teams integration to automatically import meeting transcripts and post summaries back to
            Teams channels. Requires an Azure app registration with <strong>OnlineMeetings.Read.All</strong> and{" "}
            <strong>CallRecords.Read.All</strong> Graph permissions.
          </Trans>
        </Text>
      </Alert>
      <Paper withBorder p="md">
        <Text fw={600} mb="sm">
          <Trans>Microsoft Teams / Graph API</Trans>
        </Text>
        <Stack gap="sm">
          <TextInput
            label={i18n._(msg`Tenant ID`)}
            description={i18n._(msg`Azure Entra Directory (tenant) ID`)}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            value={settings.teams_tenant_id ?? ""}
            disabled={!canManage}
            onChange={(e) => onUpdateSetting("teams_tenant_id", e.currentTarget.value)}
          />
          <TextInput
            label={i18n._(msg`App (client) ID`)}
            description={i18n._(msg`Azure app registration client ID`)}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            value={settings.teams_app_id ?? ""}
            disabled={!canManage}
            onChange={(e) => onUpdateSetting("teams_app_id", e.currentTarget.value)}
          />
          <PasswordInput
            label={
              <Group gap="xs" component="span">
                <Trans>Client secret</Trans>
                {secretConfiguredUnchanged && (
                  <Badge color="success" variant="light" size="xs">
                    <Trans>Configured</Trans>
                  </Badge>
                )}
              </Group>
            }
            description={i18n._(msg`App registration client secret value`)}
            placeholder={
              secretConfiguredUnchanged
                ? i18n._(msg`•••• saved secret — enter a new one to replace`)
                : i18n._(msg`Enter secret…`)
            }
            value={settings.teams_secret ?? ""}
            disabled={!canManage}
            onChange={(e) => onUpdateSetting("teams_secret", e.currentTarget.value)}
          />
          <TextInput
            label={i18n._(msg`Watched user IDs`)}
            description={i18n._(
              msg`Comma-separated Azure user object IDs to poll for meetings (leave blank to discover all)`,
            )}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx, …"
            value={settings.teams_watched_user_ids ?? ""}
            disabled={!canManage}
            onChange={(e) => onUpdateSetting("teams_watched_user_ids", e.currentTarget.value)}
          />
          <TextInput
            label={i18n._(msg`Teams channel webhook URL`)}
            description={i18n._(
              msg`Incoming webhook URL to post meeting summaries back to a Teams channel (optional). Posting is currently switched off platform-wide: summaries are only shared on purpose.`,
            )}
            placeholder="https://…webhook.office.com/…"
            value={settings.teams_webhook_url ?? ""}
            disabled={!canManage}
            onChange={(e) => onUpdateSetting("teams_webhook_url", e.currentTarget.value)}
          />
          {testResult && (
            <Alert color={testResult.valid ? "success" : "danger"} variant="light">
              {testResult.valid
                ? i18n._(msg`Connection successful — Graph API credentials are valid.`)
                : (testResult.error ?? i18n._(msg`Connection failed.`))}
            </Alert>
          )}
          {canManage ? (
            <Group gap="sm">
              <Button variant="default" loading={testing} disabled={!canTest} onClick={handleTest}>
                <Trans>Test connection</Trans>
              </Button>
              <Button loading={saving} onClick={() => onSave(teamsKeys)}>
                <Trans>Save</Trans>
              </Button>
            </Group>
          ) : (
            <SettingsReadOnlyNotice />
          )}
        </Stack>
      </Paper>
    </Stack>
  )
}
